import React, { useRef, useState, useEffect, useCallback } from 'react';
import '../styles/SpinWheel.css';
import { db, auth } from '../firebase';
import {
  doc,
  onSnapshot,
  runTransaction,
  updateDoc,
  arrayUnion,
} from 'firebase/firestore';
import { lookupSpinWheelRewardTile } from '../data/spinWheelRewards';
import { MAX_USER_REWARD_TILES, REWARD_TILE_LIMIT_MESSAGE } from '../utils/rewardTileLimits';
import { buildNewUserDoc, DEFAULT_USER_SPIN_BALANCE } from '../utils/newUserDefaults';
import { fetchPostedSponsoredDateIdeas } from '../utils/sponsoredDateIdeasFeed';

export { DEFAULT_USER_SPIN_BALANCE };

/** Firestore user doc when auth exists but `users/{uid}` was never written. */
function buildLazyUserDoc() {
  const user = auth.currentUser;
  const rawEmail = user?.email;
  const email = typeof rawEmail === 'string' && rawEmail.trim() ? rawEmail.trim() : null;
  return buildNewUserDoc({ email, signupSource: 'spin_wheel_lazy' });
}

const NOTHING_PRIZE = {
  label: 'Nothing',
  resultCopy: 'Next time — better luck',
  resultStyle: 'message',
  redeemable: false,
};

function prizeColorsForWheelIndex(wheelIndex) {
  return wheelIndex % 2 === 0
    ? { color: '#e3a01b', textColor: '#1a1919' }
    : { color: '#1a1919', textColor: '#fff' };
}

/** Six segments: 5 FOX-PAID DATES place titles + Nothing (middle slot). */
function buildSpinPrizesFromFoxPaidTitles(placeTitles) {
  const titles = (Array.isArray(placeTitles) ? placeTitles : [])
    .map((t) => String(t || '').trim())
    .filter(Boolean)
    .slice(0, 5);
  while (titles.length < 5) {
    titles.push('Fox-Paid Date');
  }
  const segments = [
    { label: titles[0] },
    { label: titles[1] },
    NOTHING_PRIZE,
    { label: titles[2] },
    { label: titles[3] },
    { label: titles[4] },
  ];
  return segments.map((segment, wheelIndex) => ({
    ...segment,
    ...prizeColorsForWheelIndex(wheelIndex),
  }));
}

/** Split long wedge labels into two radial lines so they fit the segment. */
function wheelLabelLines(label) {
  const s = String(label || '').trim();
  if (!s) return [''];
  const forSplit = s.match(/^(.+?)\s+(for\s.+)$/i);
  if (forSplit) return [forSplit[1].trim(), forSplit[2].trim()];
  const words = s.split(/\s+/);
  if (words.length <= 2) return [s];
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
}

function drawWheel(ctx, size, rotation, prizes) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 8;
  const segmentArc = (2 * Math.PI) / prizes.length;

  ctx.clearRect(0, 0, size, size);
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rotation);

  prizes.forEach((prize, i) => {
    const startAngle = i * segmentArc;
    const endAngle = startAngle + segmentArc;

    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, r, startAngle, endAngle);
    ctx.closePath();
    ctx.fillStyle = prize.color;
    ctx.fill();

    ctx.strokeStyle = 'rgba(255,255,255,0.15)';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.save();
    ctx.rotate(startAngle + segmentArc / 2);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = prize.textColor;
    const lines = wheelLabelLines(prize.label);
    const fontPx = Math.round(size * (lines.length > 1 ? 0.048 : 0.058));
    ctx.font = `bold ${fontPx}px 'Londrina Solid', sans-serif`;
    const lineHeight = fontPx * 1.05;
    const startY = -((lines.length - 1) * lineHeight) / 2;
    lines.forEach((line, li) => {
      ctx.fillText(line, r * 0.58, startY + li * lineHeight);
    });
    ctx.restore();
  });

  ctx.beginPath();
  ctx.arc(0, 0, 22, 0, 2 * Math.PI);
  ctx.fillStyle = '#fff';
  ctx.fill();
  ctx.strokeStyle = '#e3a01b';
  ctx.lineWidth = 3;
  ctx.stroke();

  ctx.restore();
}

/**
 * Full spin + redeem flow inline (no modal). `userId` enables Firestore `users.{uid}.spinBalance`.
 */
export default function SpinWheelInline({
  userId = null,
  maxSize = 260,
  className = '',
  triggerButtonClassName = 'spinwheel-inline-trigger',
  /** Length of `users.{uid}.rewardsToRedeem` (from parent snapshot). */
  rewardsToRedeemCount = 0,
  onRedeem,
  /** Opens vibe check chat (same as GET A PAID-FOR DATE). */
  onOutOfSpinsClick,
}) {
  const canvasRef = useRef(null);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  /** Segment `label` for the current outcome (Firestore prize id, wheel segment). */
  const [outcomeLabel, setOutcomeLabel] = useState(null);
  const rotationRef = useRef(0);
  const rafRef = useRef(null);
  const resetTimeoutRef = useRef(null);
  /** Delay before auto-adding a redeemable prize after showing "You won …". */
  const prizeRevealDelayRef = useRef(null);
  const [redeemSubmitted, setRedeemSubmitted] = useState(false);
  const [spinBalance, setSpinBalance] = useState(null);
  const [balanceKnown, setBalanceKnown] = useState(false);
  const [spinError, setSpinError] = useState('');
  const spinRewardTileAddedRef = useRef(false);
  const [prizes, setPrizes] = useState(() => buildSpinPrizesFromFoxPaidTitles([]));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ideas = await fetchPostedSponsoredDateIdeas(db);
      if (cancelled) return;
      setPrizes(buildSpinPrizesFromFoxPaidTitles(ideas.map((idea) => idea.place)));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!userId) {
      setSpinBalance(null);
      setBalanceKnown(false);
      return undefined;
    }
    const ref = doc(db, 'users', userId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const raw = snap.data()?.spinBalance;
        const n = typeof raw === 'number' && raw >= 0 ? raw : DEFAULT_USER_SPIN_BALANCE;
        setSpinBalance(n);
        setBalanceKnown(true);
      },
      (err) => {
        console.error('spinBalance snapshot:', err);
        setBalanceKnown(true);
      },
    );
    return () => unsub();
  }, [userId]);

  const sizeFn = useCallback(() => {
    if (typeof window === 'undefined') return maxSize;
    return Math.min(maxSize, Math.floor(window.innerWidth * 0.72));
  }, [maxSize]);

  const [canvasSize, setCanvasSize] = useState(() => sizeFn());

  useEffect(() => {
    const onResize = () => setCanvasSize(sizeFn());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [sizeFn]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasSize * dpr;
    canvas.height = canvasSize * dpr;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawWheel(ctx, canvasSize, rotationRef.current, prizes);
  }, [canvasSize, prizes]);

  const clearSpinOutcome = useCallback(() => {
    if (prizeRevealDelayRef.current) {
      clearTimeout(prizeRevealDelayRef.current);
      prizeRevealDelayRef.current = null;
    }
    spinRewardTileAddedRef.current = false;
    setRedeemSubmitted(false);
    setResult(null);
    setOutcomeLabel(null);
    rotationRef.current = 0;
    const canvas = canvasRef.current;
    if (canvas) {
      const dpr = window.devicePixelRatio || 1;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawWheel(ctx, canvasSize, 0, prizes);
    }
  }, [canvasSize, prizes]);

  const resetAfterRedeem = useCallback(() => {
    if (onRedeem) onRedeem();
    clearSpinOutcome();
  }, [onRedeem, clearSpinOutcome]);

  const redeemOutcomeWithLabel = useCallback(
    async (label) => {
      if (!userId || !label) return;
      if (!spinRewardTileAddedRef.current) {
        if (rewardsToRedeemCount >= MAX_USER_REWARD_TILES) {
          setSpinError(REWARD_TILE_LIMIT_MESSAGE);
          return;
        }
        try {
          const spec = lookupSpinWheelRewardTile(label);
          const idSlug = label.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 48) || 'prize';
          await updateDoc(doc(db, 'users', userId), {
            rewardsToRedeem: arrayUnion({
              id: `spin-${idSlug}-${Date.now()}`,
              source: 'spinWheel',
              spinPrizeLabel: label,
              title: spec.title,
              description: spec.description,
            }),
          });
          spinRewardTileAddedRef.current = true;
          setSpinError('');
        } catch (err) {
          console.error('spin reward tile:', err);
          setSpinError('Could not add your reward. Try again.');
          return;
        }
      }
      setRedeemSubmitted(true);
      if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = setTimeout(() => {
        resetTimeoutRef.current = null;
        resetAfterRedeem();
      }, 1200);
    },
    [userId, rewardsToRedeemCount, resetAfterRedeem],
  );

  const spin = async () => {
    if (spinning || result) return;
    if (!userId) {
      setSpinError('Sign in to spin.');
      return;
    }
    if (balanceKnown && spinBalance < 1) return;

    setSpinError('');
    setSpinning(true);

    const targetIndex = Math.floor(Math.random() * prizes.length);
    const segmentArc = (2 * Math.PI) / prizes.length;

    try {
      const spent = await runTransaction(db, async (transaction) => {
        const ref = doc(db, 'users', userId);
        const snap = await transaction.get(ref);
        if (!snap.exists()) {
          transaction.set(ref, buildLazyUserDoc(), { merge: true });
          return 'empty';
        }
        const raw = snap.data()?.spinBalance;
        const bal = typeof raw === 'number' && raw >= 0 ? raw : DEFAULT_USER_SPIN_BALANCE;
        if (bal < 1) return 'empty';
        transaction.set(ref, { spinBalance: bal - 1 }, { merge: true });
        return 'ok';
      });
      if (spent !== 'ok') {
        setSpinning(false);
        setSpinError('No spins left.');
        return;
      }
    } catch (e) {
      console.error('spin transaction:', e);
      setSpinning(false);
      setSpinError('Could not use a spin. Try again.');
      return;
    }

    const startRotation = rotationRef.current;

    const desiredPhase =
      ((-Math.PI / 2 - targetIndex * segmentArc - segmentArc / 2) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
    const currentPhase = ((startRotation % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    const phaseDelta = ((desiredPhase - currentPhase) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);

    const fullSpins = (5 + Math.floor(Math.random() * 3)) * 2 * Math.PI;
    const totalDelta = fullSpins + phaseDelta;
    const duration = 4000 + Math.random() * 1000;
    const startTime = performance.now();

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const animate = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(t);

      rotationRef.current = startRotation + totalDelta * eased;

      const canvas = canvasRef.current;
      if (canvas) {
        const dpr = window.devicePixelRatio || 1;
        const ctx = canvas.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        drawWheel(ctx, canvasSize, rotationRef.current, prizes);
      }

      if (t < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        const p = prizes[targetIndex];
        spinRewardTileAddedRef.current = false;
        setSpinning(false);
        setResult(p.resultCopy ?? p.label);
        setOutcomeLabel(p.label);

        const redeemableWin = p.redeemable !== false && p.resultStyle !== 'message';
        if (prizeRevealDelayRef.current) {
          clearTimeout(prizeRevealDelayRef.current);
          prizeRevealDelayRef.current = null;
        }
        if (redeemableWin) {
          prizeRevealDelayRef.current = setTimeout(() => {
            prizeRevealDelayRef.current = null;
            void redeemOutcomeWithLabel(p.label);
          }, 2200);
        }
      }
    };

    rafRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
      if (prizeRevealDelayRef.current) clearTimeout(prizeRevealDelayRef.current);
    };
  }, []);

  const noSpins = Boolean(userId && balanceKnown && spinBalance < 1);
  const spinButtonDisabled = spinning || !userId || noSpins;

  const outcomePrize = outcomeLabel ? prizes.find((p) => p.label === outcomeLabel) : null;
  const resultIsMessage = outcomePrize?.resultStyle === 'message';
  const outcomeRedeemable = outcomePrize ? outcomePrize.redeemable !== false : true;

  return (
    <div className={`spinwheel-inline-panel ${className}`.trim()}>
      <div className="spinwheel-stage spinwheel-stage--inline">
        <div className="spinwheel-pointer spinwheel-pointer--inline" aria-hidden="true" />
        <canvas
          ref={canvasRef}
          className="spinwheel-canvas"
          style={{ width: canvasSize, height: canvasSize }}
          aria-hidden="true"
        />
      </div>

      {result ? (
        <div className="spinwheel-result spinwheel-result--inline" role="status">
          {resultIsMessage ? (
            <span className="spinwheel-result-message">{result}</span>
          ) : (
            <span>
              You won <span className="spinwheel-result-prize">{result}</span>
            </span>
          )}
          {!outcomeRedeemable && !redeemSubmitted && (
            <button type="button" className="spinwheel-redeem spinwheel-redeem--dismiss" onClick={clearSpinOutcome}>
              OK
            </button>
          )}
          {outcomeRedeemable && !redeemSubmitted && spinError ? (
            <button type="button" className="spinwheel-redeem spinwheel-redeem--dismiss" onClick={clearSpinOutcome}>
              OK
            </button>
          ) : null}
          {redeemSubmitted && (
            <p className="spinwheel-email-thanks">Added to your rewards</p>
          )}
        </div>
      ) : (
        <button
          type="button"
          className={triggerButtonClassName}
          onClick={() => void spin()}
          disabled={spinButtonDisabled}
        >
          {spinning ? 'Spinning...' : 'SPIN & WIN'}
        </button>
      )}

      {userId ? (
        <div className="spinwheel-balance-block">
          {balanceKnown && noSpins ? (
            typeof onOutOfSpinsClick === 'function' ? (
              <button
                type="button"
                className="user-menu-collage-vibe-check-streak-hint-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onOutOfSpinsClick();
                }}
              >
                complete a vibe streak to get a{' '}
                <span className="user-menu-collage-vibe-check-streak-hint-tail">
                  spin
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="user-menu-collage-vibe-check-streak-hint-arrow"
                    aria-hidden
                  >
                    <path d="M7 17 17 7M7 7h10v10" />
                  </svg>
                </span>
              </button>
            ) : (
              <p className="spinwheel-balance-line">complete a vibe streak to get a spin</p>
            )
          ) : (
            <p className="spinwheel-balance-line" aria-live="polite">
              {balanceKnown ? (
                <>
                  You have {spinBalance} {spinBalance === 1 ? 'spin' : 'spins'}
                </>
              ) : (
                <>Checking spins…</>
              )}
            </p>
          )}
        </div>
      ) : null}

      {spinError ? (
        <p className="spinwheel-spin-error" role="alert">
          {spinError}
        </p>
      ) : null}
    </div>
  );
}
